import React, { useCallback, useMemo, useState } from 'react'
import { TextField, Button, Dropdown, Checkbox } from '@ui-library/core'
import { LoaderRegular } from '@ui-library/icons/'

import validators from './validators'
import { Wrapper, Title, Row, Col, LegalCheckboxWrapper } from './PartnerForm.style'
import RadioBoxInput from './RadioBoxInput'
import { AutocompleteAddressInput } from './AutocompleteAddressInput'

const mailRegexp = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/gim
const checkMail = val => val.toLowerCase().match(mailRegexp) !== null

const phoneFormatter = value => {
  if (value === 0) return ''
  let newValue = value.toString().replace(/[^\d]/g, '')
  const reg = /(\d)(?=(\d{2})+(?!\d))/g
  while (reg.test(newValue)) newValue = newValue.replace(reg, '$1 ')
  return newValue
}

const INITIAL_STATE = {
  addressFrom: {
    value: undefined,
    valid: true,
  },
  addressTo: {
    value: undefined,
    valid: true,
  },
  dateField: {
    value: '',
    valid: false,
  },
  surface: {
    value: '',
    valid: false,
  },
  title: {
    value: 'Monsieur',
    valid: true,
  },
  name: {
    value: '',
    valid: false,
  },
  email: {
    value: '',
    valid: false,
  },
  phone: {
    value: '',
    valid: false,
  },
  legal: {
    checked: false,
    valid: false,
  },
  optIn: {
    checked: false,
    valid: true,
  },
  propertyFound: {
    checked: false,
    valid: false,
  },
}

const TITLE_SELECT_OPTIONS = [
  {
    key: '1',
    label: 'Madame',
    value: 'Madame',
  },
  {
    key: '2',
    label: 'Monsieur',
    value: 'Monsieur',
  },
]

const PartnerForm = ({ onSubmit, isSubmitting }) => {
  const [fields, setFields] = useState(INITIAL_STATE)
  const showSubtitle = global.showNextoriesNewTitle !== undefined
  const subtitle = global.showNextoriesNewTitle ? `Recevez jusqu'à 6 devis déménagement gratuits en 48h` : `Vos devis de déménageurs gratuits et sans engagement`

  const handleSubmit = useCallback(
    event => {
      event.preventDefault()
      onSubmit(fields)
    },
    [fields],
  )

  const handleChangeZipCode = useCallback((addressValue, name) => {
    setFields(prevState => ({
      ...prevState,
      [name]: {
        value: addressValue,
        valid: true,
      },
    }))
  }, [])

  const handleChangeDate = useCallback(event => {
    const { value } = event.target
    setFields(prevState => ({
      ...prevState,
      dateField: {
        value,
        valid: true,
      },
    }))
  }, [])

  const handleChangeSurface = useCallback(
    event => {
      const { value } = event.target

      if (validators.entry.surface(event.target.value)) {
        setFields(prevState => ({
          ...prevState,
          surface: {
            value,
            valid: validators.format.surface(value),
          },
        }))
      } else {
        setFields(prevState => ({
          ...prevState,
          surface: {
            value: value > 0 ? value : '',
            valid: false,
          },
        }))
      }
    },
    [setFields, fields],
  )

  const handleChangeTitle = useCallback(
    value => {
      setFields(prevState => ({
        ...prevState,
        title: {
          value,
          valid: true,
        },
      }))
    },
    [setFields, fields],
  )

  const handleChangeName = useCallback(
    event => {
      const { value } = event.target
      if (validators.entry.name(value)) {
        setFields(prevState => ({
          ...prevState,
          name: {
            value,
            valid: validators.format.name(value),
          },
        }))
      }
    },
    [setFields, fields],
  )

  const handleChangeEmail = useCallback(
    event => {
      const { value } = event.target
      setFields(prevState => ({
        ...prevState,
        email: { value, valid: false },
      }))
    },
    [fields, setFields],
  )

  const handleCheckMail = useCallback(
    event => {
      const { value } = event.target
      setFields(prevState => ({
        ...prevState,
        email: {
          value,
          valid: checkMail(value),
        },
      }))
    },
    [fields, setFields],
  )

  const handleChangePhone = useCallback(
    event => {
      const { value } = event.target
      const unformatValue = value.toString().replace(/[^\d]/g, '')
      if (validators.entry.phone(unformatValue)) {
        setFields(prevState => ({
          ...prevState,
          phone: {
            value: unformatValue,
            valid: validators.format.phone(unformatValue),
          },
        }))
      }
    },
    [setFields, fields],
  )

  const handleChangeLegal = useCallback(
    checked => {
      setFields(prevState => ({
        ...prevState,
        legal: {
          checked,
          valid: checked,
        },
      }))
    },
    [setFields, fields],
  )

  const handleChangeOptIn = useCallback(
    checked => {
      setFields(prevState => ({
        ...prevState,
        optIn: {
          checked,
          valid: true,
        },
      }))
    },
    [setFields, fields],
  )

  const handleChangePropertyFound = useCallback(
    event => {
      const { value } = event.target
      setFields(prevState => ({
        ...prevState,
        propertyFound: {
          checked: value,
          valid: true,
        },
      }))
    },
    [setFields, fields],
  )

  const submitButton = useMemo(
    () => {
      const hasErrors = Object.values(fields).some(field => !field.valid)
      const disabled = isSubmitting || hasErrors

      return (
        <Button type="submit" icon={isSubmitting && LoaderRegular} tag="button" kind="primary" size="large" variant="normal" style={{ width: '100%' }} disabled={disabled}>
          Obtenir mes devis de déménageurs
        </Button>
      )
    },
    [fields, isSubmitting],
  )

  const currentDate = useMemo(() => {
    const date = new Date()
    return date.toISOString().substring(0, 10)
  }, [])

  return (
    <Wrapper>
      {showSubtitle ? <Title>{subtitle}</Title> : null}
      <form onSubmit={handleSubmit} autoComplete="off">
        <Row>
          <Col>
            <AutocompleteAddressInput name="addressFrom" label="Adresse de départ" onSelect={handleChangeZipCode} />
          </Col>
          <Col>
            <AutocompleteAddressInput name="addressTo" label="Adresse d’arrivée" onSelect={handleChangeZipCode} />
          </Col>
        </Row>
        <Row mobile>
          <Col mobile>
            <TextField
              label="Date estimative du déménagement"
              onChange={handleChangeDate}
              value={fields.dateField.value}
              state={!!fields.dateField.value && !fields.dateField.valid ? 'error' : 'normal'}
              errorMessage="Date non valide"
              min={currentDate}
              type="date"
            />
          </Col>
          <Col mobile>
            <TextField
              label="Surface en m² du logement"
              onChange={handleChangeSurface}
              value={fields.surface.value !== undefined ? fields.surface.value.toString() : ''}
              state={!!fields.surface.value && !fields.surface.valid ? 'error' : 'normal'}
              errorMessage="Surface non valide"
              type="tel"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Dropdown label="Civilité" list={TITLE_SELECT_OPTIONS} value={fields.title.value} onSelect={handleChangeTitle} listAlignment="left" />
          </Col>
          <Col>
            <TextField
              label="Nom"
              onChange={handleChangeName}
              value={fields.name.value || ''}
              state={!!fields.name.value && !fields.name.valid ? 'error' : 'normal'}
              errorMessage="Nom saisi non valide"
              type="text"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextField
              label="Email"
              onChange={handleChangeEmail}
              onBlur={handleCheckMail}
              value={fields.email.value || ''}
              state={!!fields.email.value && !fields.email.valid ? 'error' : 'normal'}
              errorMessage="Adresse e-mail non valide"
              type="email"
            />
          </Col>
          <Col>
            <TextField
              label="Téléphone"
              onChange={handleChangePhone}
              value={fields.phone.value ? phoneFormatter(fields.phone.value) : ''}
              state={!!fields.phone.value && !fields.phone.valid ? 'error' : 'normal'}
              errorMessage="Numéro de téléphone non valide"
              type="tel"
            />
          </Col>
        </Row>
        <Row>
          <RadioBoxInput name="propertyFound" position="right" handleChange={handleChangePropertyFound} label="J’ai trouvé mon bien" controlled={false} />
        </Row>
        <Row>
          <LegalCheckboxWrapper>
            <Checkbox onChange={handleChangeLegal} label={null} checked={fields.legal.checked} variant="normal" size="small" style={{ marginTop: '2px' }} />
            <div>Je souhaite être contacté par email ou téléphone par Nextories afin d&#39;obtenir un devis de déménagement.</div>
          </LegalCheckboxWrapper>
        </Row>
        <Row>
          <LegalCheckboxWrapper>
            <Checkbox onChange={handleChangeOptIn} label={null} checked={fields.optIn.checked} variant="normal" size="small" style={{ marginTop: '2px' }} />
            <div>Je souhaite être contacté pour découvrir des offres sur l’énergie.</div>
          </LegalCheckboxWrapper>
        </Row>

        {submitButton}
      </form>
    </Wrapper>
  )
}

export default PartnerForm
